import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Calendar, Clock, User, Phone, Mail, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import clinicInfo from '../config/clinicInfo';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { buildTenantPath } from '../utils/tenantSlug';
import { buildWhatsAppUrl } from '../utils/contact';

interface AppointmentForm {
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  type: string;
  notes: string;
}

const AppointmentPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { profile, effectiveTenantSlug, isTenantContext } = useClinicProfile();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<AppointmentForm>();

  const appointmentTypes = [
    'Reabilitação Pulmonar',
    'Reabilitação Cardíaca',
    'Reabilitação Oncológica',
    'Fisioterapia Respiratória',
    'Avaliação Inicial'
  ];

  const availableTimes = [
    '07:30',
    '08:30',
    '09:30',
    '10:30',
    '11:30',
    '13:30',
    '14:30',
    '15:30',
    '16:30',
    '17:30'
  ];

  const formData = watch();
  const clinicDisplayName = isTenantContext
    ? profile?.displayName || 'a clínica'
    : clinicInfo.nomeFantasia;
  const contactWhatsappFull = isTenantContext
    ? profile?.contactWhatsappFull || profile?.contactPhoneFull || profile?.contactWhatsapp || profile?.contactPhone || ''
    : clinicInfo.contato.whatsappFull;
  const contactEmail = isTenantContext
    ? profile?.contactEmail || ''
    : clinicInfo.contato.email;
  const tenantHomePath = buildTenantPath('/', effectiveTenantSlug);

  const formatMessage = (data: AppointmentForm) => {
    return `
Olá,

Gostaria de agendar uma consulta com ${clinicDisplayName} com os seguintes detalhes:

Nome: ${data.name}
Email: ${data.email}
Telefone: ${data.phone}
Tipo de Atendimento: ${data.type}
Data Preferencial: ${data.date}
Horário Preferencial: ${data.time}

${data.notes ? `Observações Adicionais:\n${data.notes}` : ''}

Aguardo retorno.
Obrigado(a)!`;
  };

  const handleWhatsApp = () => {
    const message = formatMessage(formData);
    const whatsappLink = buildWhatsAppUrl(contactWhatsappFull, message);

    if (!whatsappLink) {
      toast.error('Este canal de contato está indisponível no momento.');
      return;
    }

    window.open(whatsappLink, '_blank');
    toast.success('Redirecionando para o WhatsApp...');
    setTimeout(() => navigate(tenantHomePath), 2000);
  };

  const handleEmail = () => {
    if (!contactEmail) {
      toast.error('O contato por e-mail está indisponível no momento.');
      return;
    }

    const message = formatMessage(formData);
    const subject = encodeURIComponent(`Solicitação de Agendamento - ${formData.type}`);
    const mailtoLink = `mailto:${contactEmail}?subject=${subject}&body=${encodeURIComponent(message)}`;
    window.location.href = mailtoLink;
    toast.success('Abrindo seu cliente de email...');
    setTimeout(() => navigate(tenantHomePath), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Agende sua Consulta</h1>
          <p className="text-lg text-gray-600">
            Preencha o formulário abaixo para solicitar seu agendamento. Nossa equipe entrará em contato para confirmar o horário.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Informações Pessoais</h2>
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nome Completo
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      {...register('name', { required: 'Nome é obrigatório' })}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Telefone
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      {...register('phone', { required: 'Telefone é obrigatório' })}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              {/* Appointment Details */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">Detalhes da Consulta</h2>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                    Tipo de Atendimento
                  </label>
                  <select
                    id="type"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    {...register('type', { required: 'Tipo de atendimento é obrigatório' })}
                  >
                    <option value="">Selecione o tipo de atendimento</option>
                    {appointmentTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Data Preferencial
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="date"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      {...register('date', { required: 'Data é obrigatória' })}
                    />
                  </div>
                  {errors.date && (
                    <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                    Horário Preferencial
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="time"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      {...register('time', { required: 'Horário é obrigatório' })}
                    >
                      <option value="">Selecione o horário</option>
                      {availableTimes.map((time) => (
                        <option key={time} value={time}>{time}h</option>
                      ))}
                    </select>
                  </div>
                  {errors.time && (
                    <p className="mt-1 text-sm text-red-600">{errors.time.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Observações Adicionais
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="notes"
                  rows={4}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Informe qualquer informação adicional relevante para o atendimento"
                  {...register('notes')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={handleWhatsApp}
                disabled={isSubmitting || !contactWhatsappFull}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Phone className="w-5 h-5 mr-2" />
                Solicitar via WhatsApp
              </button>

              <button
                type="button"
                onClick={handleEmail}
                disabled={isSubmitting || !contactEmail}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Mail className="w-5 h-5 mr-2" />
                Solicitar via Email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentPage;
